import { useState, useRef, useEffect, type ReactNode } from 'react';
import { ChevronDown } from '@bn-apps/design-system-icons';
import styles from './Accordion.module.scss';

interface AccordionProps {
  heading: string;
  children: ReactNode;
  defaultOpen?: boolean;
  className?: string;
}

export function Accordion({ heading, children, defaultOpen = false, className }: AccordionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const measure = () => setContentHeight(el.scrollHeight);
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [children]);

  return (
    <div className={[styles.accordion, isOpen ? styles.accordionOpen : '', className].filter(Boolean).join(' ')}>
      <h3 className={styles.headingWrapper}>
        <button
          type="button"
          className={styles.trigger}
          aria-expanded={isOpen}
          onClick={() => setIsOpen((prev) => !prev)}
        >
          <span className={`${styles.chevronWrap} ${isOpen ? styles.chevronWrapOpen : ''}`} aria-hidden="true">
            <ChevronDown />
          </span>
          <span className={styles.headingText}>{heading}</span>
        </button>
      </h3>
      <div
        className={`${styles.panel} ${isOpen ? styles.panelOpen : ''}`}
        role="region"
        style={{ maxHeight: isOpen ? contentHeight : 0 }}
      >
        <div ref={contentRef} className={styles.panelInner}>
          {children}
        </div>
      </div>
    </div>
  );
}
